import { Link } from 'react-router-dom';
import ProductCard from '@/components/feature/ProductCard';
import ArticleCard from '@/components/feature/ArticleCard';
import { products, categories, articles, testimonials, brandFeatures, categoryNameMap } from '@/mocks/data';
import { getOrganizationSchema, getWebSiteSchema, getWebPageSchema, getFAQSchema } from '@/lib/seo';

const featuredProducts = products.slice(0, 6);
const hotArticles = articles.slice(0, 3);

const faqItems = [
  {
    question: '国产精品精选推荐平台是什么？',
    answer: '国产精品精选推荐是一个专注于甄选和推荐国产优质产品的平台。我们严选品质标准，涵盖美妆护肤、家居生活、数码科技、服饰配饰、美食饮品、母婴用品等六大核心品类，致力于帮助消费者发现真正值得信赖的国货好物。',
  },
  {
    question: '平台上的产品是如何筛选的？',
    answer: '每一款产品都经过我们严格的品质审核流程。我们从原料溯源、生产工艺、成品检测到用户口碑进行全维度评估，确保只有真正经得起考验的产品才能呈现在您面前。',
  },
  {
    question: '平台支持哪些品类？',
    answer: '目前平台涵盖六大核心品类：美妆护肤、家居生活、数码科技、服饰配饰、美食饮品和母婴用品。每个品类都精选了行业内最具代表性的优质国货品牌。',
  },
  {
    question: '如何联系客服？',
    answer: '您可以通过关于我们页面的联系表单发送留言，或发送邮件至 contact@anidndd.cn，我们的客服团队会在24小时内回复您的咨询。',
  },
];

export default function Home() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getOrganizationSchema()) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getWebSiteSchema()) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getWebPageSchema(
              '国产精品精选推荐 | 品质之选尽在这里',
              '探索最值得信赖的国产精品，涵盖多品类优质国货好物，严选品质标准，为您推荐真正经得起考验的国产精品。',
              'https://anidndd.cn/'
            )
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getFAQSchema(faqItems)) }}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20minimal%20cream%20background%20with%20subtle%20warm%20golden%20light%20and%20soft%20organic%20textures%2C%20refined%20luxury%20aesthetic%2C%20clean%20and%20spacious%20composition%20with%20gentle%20gradients%2C%20perfect%20for%20product%20showcase&width=1800&height=1200&seq=hero-main&orientation=landscape"
            alt="国产精品"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/30"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-center text-background-50 leading-tight">
            品质之选尽在这里
          </h1>
          <p className="mt-4 md:mt-6 text-sm md:text-lg text-background-200 max-w-2xl text-center leading-relaxed">
            严选品质国货好物，从原料到工艺，从设计到服务，让每一件国产精品都成为您生活品质的见证
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3 mt-8">
            <Link
              to="/category/meirong-hufu"
              className="bg-primary-500 text-background-50 px-8 py-3 rounded-md font-medium text-sm md:text-base hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              探索精选好物
            </Link>
            <Link
              to="/about"
              className="bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 px-8 py-3 rounded-md font-medium text-sm md:text-base hover:bg-background-50/30 transition-colors whitespace-nowrap cursor-pointer"
            >
              了解我们
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Features */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {brandFeatures.map((feature, index) => (
              <div key={index} className="flex flex-col items-center text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-3">
                  <i className={`${feature.icon} text-xl text-primary-600`}></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1.5">
                  {feature.title}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              精选分类
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              六大品类，总有一款适合您
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center p-5 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-secondary-100 group-hover:bg-primary-100 transition-colors">
                  <i className={`${cat.icon} text-2xl text-secondary-600 group-hover:text-primary-600 transition-colors`}></i>
                </div>
                <span className="mt-3 font-heading text-sm font-semibold text-foreground-900 whitespace-nowrap">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                精选推荐
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                严选品质，为您甄选
              </p>
            </div>
            <Link
              to="/category/meirong-hufu"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer"
            >
              查看全部
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-product-shop>
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} {...product} />
            ))}
          </div>
        </div>
      </section>

      {/* Hot Articles */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                品质资讯
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                了解国货精品的最新动态
              </p>
            </div>
            <Link
              to="/article/1"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer"
            >
              更多文章
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {hotArticles.map((article) => (
              <ArticleCard
                key={article.id}
                {...article}
                categoryName={categoryNameMap[article.category]}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              用户口碑
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              来自真实用户的使用体验分享
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {testimonials.map((item) => (
              <div
                key={item.id}
                className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6"
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: item.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  &ldquo;{item.content}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                    <p className="text-xs text-foreground-400">{item.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="relative rounded-xl overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimal%20cream%20background%20with%20soft%20elegant%20golden%20accents%20and%20subtle%20texture%2C%20luxurious%20and%20inviting%20atmosphere%2C%20clean%20composition&width=1400&height=500&seq=cta-home&orientation=landscape"
                alt="品质生活"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-black/20"></div>
            <div className="relative z-10 py-14 md:py-20 px-6 md:px-12 text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
                开启您的品质生活之旅
              </h2>
              <p className="text-sm md:text-base text-background-200 max-w-xl mx-auto mb-6">
                发现更多值得信赖的国产精品，让品质成为您生活的常态
              </p>
              <Link
                to="/category/meirong-hufu"
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-8 py-3 rounded-md font-medium text-sm md:text-base hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即探索
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}